// Shared per-tab WebSocket multiplexer for cockpit widgets.
//
// Why this exists: every widget instance used to open its own useWebSocket
// connection. With multi-instance cockpit (10+ widgets per tab realistic),
// that meant 10+ TCP sockets per user × thousands of users — server-side
// resource explosion plus duplicated inbound data for the same coin.
//
// This module replaces that with one connection per browser tab. Widgets
// register interest via `acquire(coin)`; refcount drops to zero → server-
// side subscription released. Inbound events route into a per-coin store
// (see tradeStore.js); widgets subscribe via selector hooks so only the
// affected slice re-renders.
//
// Inbound throttling: WS can fire 100+ messages/sec during fast markets.
// We buffer them and flush once per requestAnimationFrame so React tops
// out at ~60Hz updates regardless of input rate.
//
// Lifecycle: connection lazy-opens on first `acquire`. Stays open while
// at least one coin has refcount > 0. Auto-reconnects with backoff on
// drop. Closes only when the whole page unloads (no idle disconnect — we
// often re-acquire instantly when users toggle widgets).

import { WS_BASE_URL } from '../../../utils/apiConfig';
import { getStoredUserId } from '../../../utils/analytics';
import { tradeStore } from './tradeStore';

const MAX_TRADES_PER_COIN = 1000;
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30000;

let ws = null;
let isConnecting = false;
let reconnectTimer = null;
let reconnectDelay = RECONNECT_MIN_MS;

// coin -> refcount. Tracks how many widget instances currently want updates
// for a given coin. Used to drive the server subscription set.
const refs = new Map();

// Bookkeeping for what we've actually told the server about so we can
// diff and send the minimum mutation on each change.
const serverSubscribed = new Set();
let serverFiltersSent = false;

// Inbound event queue, flushed in a single rAF tick to coalesce React
// state updates.
let pendingFlush = false;
const pendingTrades = []; // { coin, kind: 'whale'|'liq', trade }
const pendingHistoricalByCoin = new Map(); // coin -> { whaleTrades, liquidations, summary }
const pendingSummaryByCoin = new Map(); // coin -> summary

// Sound playback handlers — injected from React-land (useTradeStreamSounds)
// because tradeStream is a module singleton and can't read context. Live
// trades flushed below call into these; historical dumps don't (otherwise
// every page load would play a burst of alerts for stale data).
let soundHandlers = null;

export function setSoundHandlers(handlers) {
  soundHandlers = handlers || null;
}

// Pure helper: decide which sound handlers to invoke for a single live
// trade event. Exported so the tradeStream → sound bridge can be unit
// tested without spinning up a WebSocket. `handlers` must expose
// { getTier, playTieredSound, playMillionAlert }.
export function triggerLiveTradeSounds(ev, handlers) {
  if (!handlers || !ev || !ev.trade) return;
  const t = ev.trade;
  const meta = { coin: ev.coin, side: t.side, exchange: t.exchange };
  try {
    if (ev.kind === 'whale') {
      const tier = handlers.getTier?.(t.dollarSize);
      if (tier) handlers.playTieredSound?.(t.dollarSize, 'whale', meta);
      handlers.playMillionAlert?.(t.side, t.dollarSize, meta);
    } else if (ev.kind === 'liq') {
      const tier = handlers.getTier?.(t.dollarSize);
      if (tier) handlers.playTieredSound?.(t.dollarSize, 'liquidation', meta);
    }
  } catch { /* sound errors must not break the flush */ }
}

function wsUrl() {
  // Same path the old hook used. Reusing avoids any server-side routing
  // surprises while we migrate widgets onto this transport.
  const userId = (typeof window !== 'undefined' && getStoredUserId)
    ? getStoredUserId()
    : null;
  const base = WS_BASE_URL || (typeof window !== 'undefined'
    ? (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host
    : 'ws://localhost:3000');
  return `${base}/ws${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`;
}

function ensureConnection() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  if (isConnecting) return;
  isConnecting = true;
  clearTimeout(reconnectTimer);

  try {
    ws = new WebSocket(wsUrl());
  } catch (err) {
    isConnecting = false;
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    isConnecting = false;
    reconnectDelay = RECONNECT_MIN_MS;
    // Reset what the server knows so syncSubscriptions sends fresh state.
    serverSubscribed.clear();
    serverFiltersSent = false;
    syncSubscriptions();
    emitStatusIfChanged();
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      route(msg);
    } catch {
      // Malformed payload — drop silently.
    }
  };

  ws.onclose = () => {
    isConnecting = false;
    ws = null;
    emitStatusIfChanged();
    if (refs.size > 0) scheduleReconnect();
  };

  ws.onerror = () => {
    // onclose handles reconnect — we don't double-schedule here.
  };
}

function scheduleReconnect() {
  clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => {
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
    ensureConnection();
  }, reconnectDelay);
}

// Reconcile what the server is subscribed to against the current refcounts.
// Cheap diff: O(unique coins). Sends one filter_update + N subscribe_coin/
// unsubscribe_coin calls.
function syncSubscriptions() {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;

  const wantedCoins = new Set([...refs.keys()].filter((c) => refs.get(c) > 0));

  // First time on this connection: send the initial subscribe with the
  // tradeTypes / filters frame so the server starts streaming.
  if (!serverFiltersSent) {
    ws.send(JSON.stringify({
      type: 'subscribe',
      filters: {
        whaleTradesMinVolume: 0,
        liquidationsMinVolume: 0,
        tradeTypes: ['whale_trade', 'liquidation'],
        selectedCoins: [...wantedCoins],
      },
    }));
    serverFiltersSent = true;
  } else {
    // Existing connection — push updated coin set so server filters
    // inbound stream accordingly.
    ws.send(JSON.stringify({
      type: 'update_filters',
      filters: {
        whaleTradesMinVolume: 0,
        liquidationsMinVolume: 0,
        tradeTypes: ['whale_trade', 'liquidation'],
        selectedCoins: [...wantedCoins],
      },
    }));
  }

  // For each newly-wanted coin, ask for history + subscribe to live tape.
  for (const coin of wantedCoins) {
    if (serverSubscribed.has(coin)) continue;
    ws.send(JSON.stringify({
      type: 'request_coin_data',
      coin,
      limit: 200,
      offset: 0,
      tradeTypes: ['whale_trade', 'liquidation'],
      whaleMinSize: 0,
      liqMinSize: 0,
    }));
    ws.send(JSON.stringify({ type: 'subscribe_coin', coin }));
    serverSubscribed.add(coin);
  }

  // For each no-longer-wanted coin, unsubscribe.
  for (const coin of serverSubscribed) {
    if (wantedCoins.has(coin)) continue;
    ws.send(JSON.stringify({ type: 'unsubscribe_coin', coin }));
    serverSubscribed.delete(coin);
  }
}

function route(msg) {
  switch (msg.type) {
    case 'coin_data':
      // Initial history dump for a coin. Replace the slice atomically.
      pendingHistoricalByCoin.set(msg.coin, {
        whaleTrades: Array.isArray(msg.whaleTrades) ? msg.whaleTrades : [],
        liquidations: Array.isArray(msg.liquidations) ? msg.liquidations : [],
        summary: msg.summary || null,
      });
      scheduleFlush();
      break;
    case 'coin_trade': {
      const kind = msg.tradeType === 'whale_trade' ? 'whale'
        : msg.tradeType === 'liquidation' ? 'liq' : null;
      if (kind && msg.trade) {
        const ev = { coin: msg.coin, kind, trade: msg.trade };
        // Sound playback must run synchronously on ws.onmessage, NOT
        // inside the rAF flush. Chrome page-occlusion tracking pauses
        // rAF when the window is minimized or occluded, even while
        // document.visibilityState stays 'visible' (common on macOS).
        // The /trades hook dispatches sound directly from onmessage —
        // mirror that so /chart matches its behaviour.
        triggerLiveTradeSounds(ev, soundHandlers);
        pendingTrades.push(ev);
      }
      scheduleFlush();
      break;
    }
    case 'coin_summary':
      if (msg.summary) {
        pendingSummaryByCoin.set(msg.coin, msg.summary);
        scheduleFlush();
      }
      break;
    default:
      // Ignore global feed types — cockpit widgets only consume per-coin data.
      break;
  }
}

function scheduleFlush() {
  if (pendingFlush) return;
  pendingFlush = true;
  // rAF caps React update rate at the display refresh — typically 60Hz.
  // Even if WS pushes 500 msg/sec, React rerenders ~60×/sec max.
  // Hidden tabs throttle rAF to ~1Hz, giving free backgrounded behaviour.
  requestAnimationFrame(flush);
}

function flush() {
  pendingFlush = false;

  // Apply historical dumps first — they replace, so they shouldn't be
  // clobbered by later incremental appends in the same frame.
  if (pendingHistoricalByCoin.size > 0) {
    for (const [coin, payload] of pendingHistoricalByCoin) {
      tradeStore.setCoinHistory(coin, payload, MAX_TRADES_PER_COIN);
    }
    pendingHistoricalByCoin.clear();
  }

  // Incremental trades — append, then trim.
  if (pendingTrades.length > 0) {
    // Group by coin to amortise store mutations.
    const byCoin = new Map();
    for (const ev of pendingTrades) {
      let bucket = byCoin.get(ev.coin);
      if (!bucket) { bucket = { whale: [], liq: [] }; byCoin.set(ev.coin, bucket); }
      bucket[ev.kind].push(ev.trade);
      // Sound playback already fired synchronously in route() — don't
      // re-trigger here or every alert would double up.
    }
    pendingTrades.length = 0;
    for (const [coin, bucket] of byCoin) {
      tradeStore.appendTrades(coin, bucket, MAX_TRADES_PER_COIN);
    }
  }

  // Summary updates last so they reflect the new totals.
  if (pendingSummaryByCoin.size > 0) {
    for (const [coin, summary] of pendingSummaryByCoin) {
      tradeStore.setSummary(coin, summary);
    }
    pendingSummaryByCoin.clear();
  }
}

// === Public API ===

export function acquireCoin(coin) {
  if (!coin) return;
  refs.set(coin, (refs.get(coin) || 0) + 1);
  tradeStore.ensureSlice(coin);
  ensureConnection();
  // First subscriber for a coin → push subscription set out.
  if (refs.get(coin) === 1 && ws && ws.readyState === WebSocket.OPEN) {
    syncSubscriptions();
  }
}

export function releaseCoin(coin) {
  if (!coin) return;
  const next = (refs.get(coin) || 0) - 1;
  if (next <= 0) {
    refs.delete(coin);
    if (ws && ws.readyState === WebSocket.OPEN) syncSubscriptions();
    // Keep the slice in store so a re-acquire shows last-known data
    // immediately while history reloads. Memory cost is bounded by
    // MAX_TRADES_PER_COIN trims.
  } else {
    refs.set(coin, next);
  }
}

// Diagnostics — useful for the cap-warning toast and for tests.
export function _stats() {
  return {
    connected: !!ws && ws.readyState === WebSocket.OPEN,
    readyState: ws ? ws.readyState : -1,
    refs: Object.fromEntries(refs),
    serverSubscribed: [...serverSubscribed],
  };
}

// Module-singleton check — Vite HMR can occasionally give us a fresh
// copy of this file, which would silently fork the connection. Exposing
// the refs map on window makes the duplication detectable.
if (typeof window !== 'undefined') {
  if (window.__cockpitTradeStreamRefs && window.__cockpitTradeStreamRefs !== refs) {
    // eslint-disable-next-line no-console
    console.warn('[cockpit] tradeStream module re-instantiated — possible HMR fork');
  }
  window.__cockpitTradeStreamRefs = refs;
}

// Lightweight connection-status subscription for things like the status
// bar dot. We track minimal state (connected boolean only) and fan out
// via a tiny listener set — same pattern as the per-coin tradeStore but
// global.
const statusListeners = new Set();
let lastEmittedConnected = false;

function emitStatusIfChanged() {
  const cur = !!ws && ws.readyState === WebSocket.OPEN;
  if (cur === lastEmittedConnected) return;
  lastEmittedConnected = cur;
  for (const fn of [...statusListeners]) {
    try { fn(cur); } catch { /* listener errors ignored */ }
  }
}

export function subscribeStatus(listener) {
  statusListeners.add(listener);
  // Fire immediately so consumers don't need to poll for initial value.
  listener(!!ws && ws.readyState === WebSocket.OPEN);
  return () => statusListeners.delete(listener);
}

